import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import apiFetch from './api';
import { useAuth } from './AuthContext';

const defaultTutorialContext = {
  runTutorial: false,
  startTutorial: () => {},
  stopTutorial: () => {},
  tutorialStepIndex: 0,
  setTutorialStepIndex: () => {},
  tutorialDrawerOpen: false,
  openTutorialDrawer: () => {},
  closeTutorialDrawer: () => {},
  activeTutorialGuideId: null,
  setActiveTutorialGuideId: () => {},
  guideRoleAssignments: {},
  setGuideRoleAssignments: () => {},
  tutorialSettingsLoading: false,
  tutorialSettingsError: '',
  fetchTutorialGuideRoles: async () => ({}),
};

const TutorialContext = createContext(defaultTutorialContext);

export const useTutorial = () => useContext(TutorialContext);

export const TutorialProvider = ({ children }) => {
  const { user, logout } = useAuth();
  const [runTutorial, setRunTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialDrawerOpen, setTutorialDrawerOpen] = useState(false);
  const [activeTutorialGuideId, setActiveTutorialGuideId] = useState(null);
  const [guideRoleAssignments, setGuideRoleAssignments] = useState({});
  const [tutorialSettingsLoading, setTutorialSettingsLoading] = useState(false);
  const [tutorialSettingsError, setTutorialSettingsError] = useState('');
  const location = useLocation();

  const isStaffTutorialUser = user?.user_type === 'staff' && [1, 2, 3].includes(Number(user?.role));

  const fetchTutorialGuideRoles = useCallback(async () => {
    if (!isStaffTutorialUser) {
      setGuideRoleAssignments({});
      setTutorialSettingsError('');
      return {};
    }

    setTutorialSettingsLoading(true);
    setTutorialSettingsError('');
    try {
      const response = await apiFetch('/settings/tutorial-guide-roles', {}, { onUnauthorized: logout });
      if (!response.ok) throw new Error('Failed to load tutorial guide roles');
      const data = await response.json();
      const assignments = data?.assignments && typeof data.assignments === 'object'
        ? data.assignments
        : {};
      setGuideRoleAssignments(assignments);
      return assignments;
    } catch (error) {
      setTutorialSettingsError(error.message || 'Failed to load tutorial guide roles');
      return {};
    } finally {
      setTutorialSettingsLoading(false);
    }
  }, [isStaffTutorialUser, logout]);

  useEffect(() => {
    let cancelled = false;
    if (!isStaffTutorialUser) {
      setGuideRoleAssignments({});
      setTutorialSettingsLoading(false);
      setTutorialSettingsError('');
      return undefined;
    }

    (async () => {
      const assignments = await fetchTutorialGuideRoles();
      if (cancelled) return;
      setGuideRoleAssignments(assignments || {});
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchTutorialGuideRoles, isStaffTutorialUser]);

  // Reset tutorial state if navigation happens while it's not explicitly running
  useEffect(() => {
    if (!runTutorial) {
      setTutorialStepIndex(0);
    }
  }, [location, runTutorial]);

  const startTutorial = () => {
    setTutorialStepIndex(0);
    setRunTutorial(true);
  };

  const stopTutorial = () => {
    setRunTutorial(false);
  };

  const openTutorialDrawer = (guideId = null) => {
    if (guideId) setActiveTutorialGuideId(guideId);
    setTutorialDrawerOpen(true);
  };

  const closeTutorialDrawer = () => {
    setTutorialDrawerOpen(false);
  };

  return (
    <TutorialContext.Provider
      value={{
        runTutorial,
        startTutorial,
        stopTutorial,
        tutorialStepIndex,
        setTutorialStepIndex,
        tutorialDrawerOpen,
        openTutorialDrawer,
        closeTutorialDrawer,
        activeTutorialGuideId,
        setActiveTutorialGuideId,
        guideRoleAssignments,
        setGuideRoleAssignments,
        tutorialSettingsLoading,
        tutorialSettingsError,
        fetchTutorialGuideRoles,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};
